/**
 * Session bootstrap provider.
 *
 * Fetches ``GET /api/me/bootstrap`` once on mount. Three render
 * states:
 *
 * - **loading** — shows a minimal skeleton so the user sees
 *   activity rather than a blank page.
 * - **unauthenticated** — full-page redirect to ``/api/auth/login``
 *   (the BFF's PKCE flow). The user never sees a "login page" in
 *   the UI itself — Keycloak is the login surface per ADR-009.
 * - **authenticated** — children render inside a populated
 *   :class:`SessionContext`.
 *
 * The provider also pre-populates the TanStack Query cache with
 * the bootstrap payload's preferences so Stream B's preferences
 * hook reads from cache without an extra fetch.
 *
 * Cross-references:
 *   - REQ-AUTH-057 / 062 (bootstrap response).
 *   - REQ-UI-044 (theme applied before first paint via the
 *     pre-bootstrap inline script; the provider reconciles
 *     against the server-side value after mount).
 *   - Flow 01 (login flow — the redirect part).
 *   - Flow N (logged-out / session-expired — the 401 path).
 */
import { type ReactNode, useEffect, useMemo, useState } from "react";
import { ApiError, apiFetch, redirectToLogin } from "@/lib/api-client";
import type { BootstrapResponse, Role, SessionContextValue } from "@/auth/session-types";
import { readDeploymentNetwork } from "@/auth/session-types";
import { SessionContext } from "@/auth/session-context";
import { PreferencesProvider } from "@/preferences/preferences-provider";
import { ProfilesProvider } from "@/profiles/profiles-provider";

type LoadState =
  | { status: "loading" }
  | { status: "authenticated"; bootstrap: BootstrapResponse }
  | { status: "error"; message: string };

export function SessionProvider({ children }: { children: ReactNode }): React.ReactElement | null {
  const [state, setState] = useState<LoadState>({ status: "loading" });

  useEffect(() => {
    let cancelled = false;
    apiFetch<BootstrapResponse>("/api/me/bootstrap", {
      redirectOnUnauthenticated: false,
    })
      .then((bootstrap) => {
        if (cancelled) {
          return;
        }
        setState({ status: "authenticated", bootstrap });
      })
      .catch((error) => {
        if (cancelled) {
          return;
        }
        if (error instanceof ApiError && error.status === 401) {
          // Full-page redirect — the BFF handles the OIDC dance.
          redirectToLogin();
          return;
        }
        setState({
          status: "error",
          message: error instanceof Error ? error.message : "bootstrap failed",
        });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const contextValue = useMemo<SessionContextValue | null>(() => {
    if (state.status !== "authenticated") {
      return null;
    }
    const roles = new Set<Role>(state.bootstrap.user.roles);
    return {
      user: state.bootstrap.user,
      network: readDeploymentNetwork(),
      hasRole: (role) => roles.has(role),
      hasAnyRole: (toCheck) => toCheck.some((role) => roles.has(role)),
    };
  }, [state]);

  if (state.status === "loading") {
    // Render nothing while the bootstrap fetch is in flight. An
    // unauthenticated user is redirected to Keycloak the moment the
    // fetch 401s (below), so a Meridian "Loading…" splash would only
    // flash before the redirect — we omit it (removed 2026-05-29). The
    // pre-bootstrap inline script in index.html has already applied the
    // themed background, so the brief blank is theme-correct.
    return null;
  }
  if (state.status === "error") {
    return <BootstrapError message={state.message} />;
  }

  // ProfilesProvider nests inside SessionContext (so it can read
  // the deployment network for export) and is seeded from the
  // bootstrap ``profiles`` list — the same bootstrap-co-located
  // pattern as PreferencesProvider. The gear-menu Profiles submenu
  // (REQ-UI-074) reads it via ``useProfiles()``. Mounting here
  // rather than in main.tsx keeps the bootstrap seed in the one
  // place the payload is in hand (ADR-033 §4.2; REQ-AUTH-065).
  //
  // PreferencesProvider also receives the ``profiles`` list so it can
  // locate the built-in default Global Profile for first-run
  // auto-apply (D9 / REQ-UI-093) — the same bootstrap payload, no
  // extra round-trip.
  return (
    <SessionContext.Provider value={contextValue}>
      <PreferencesProvider
        initial={state.bootstrap.preferences}
        profiles={state.bootstrap.profiles}
      >
        <ProfilesProvider initial={state.bootstrap.profiles}>{children}</ProfilesProvider>
      </PreferencesProvider>
    </SessionContext.Provider>
  );
}

function BootstrapError({ message }: { message: string }): React.ReactElement {
  // Per REQ-UI-037 — generic user-facing copy. The internal
  // diagnostic ``message`` is rendered behind a ``data-debug``
  // attribute that browser-extension diagnostics can read but
  // isn't visible by default.
  return (
    <div
      role="alert"
      data-debug={message}
      className="flex h-full flex-col items-center justify-center gap-2 bg-background text-foreground"
    >
      <p className="text-sm">We couldn't load your Meridian session.</p>
      <button
        type="button"
        onClick={() => window.location.reload()}
        className="text-body-sm text-brand underline"
      >
        Reload
      </button>
    </div>
  );
}
